import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Icon, IconButton, Menu, ModalTrigger } from '@zillow/constellation-10';
import { IconMoreFilled, IconSettingsFilled } from '@zillow/constellation-icons';

import { useGetPropertyDetailsOverview } from '../../hooks/queries/useGetPropertyDetailsOverview';
import { useSelector } from '../../selectors/useSelector';

import CorrectMapPinCSEvents from '../../model/ClickstreamEvent/CorrectMapPinCSEvents';

import AddPropertyModal from '../../../../src/components/modals/AddPropertyModal';

import AboutAddressEditsModal from '../Modal/AboutAddressEditsModal';
import ArchivePropertyModal from '../Modal/ArchivePropertyModal';
import CloneThisListingConversionModal from '../Modal/CloneThisListingConversionModal';
import EditPropertyTypeModal from '../Modal/EditPropertyTypeModal';
import MapPinAdjustmentRequestModal from '../Modal/MapPinAdjustmentRequestModal';

import type { GetUserPropertyOverviewResponseWebDto } from '@zg-rentals/rental-manager-env';

const EditPropertyTypeMenu = () => {
  const { isPostingPathAdmin, listingAlias, user, currentProperty } = useSelector(
    (state) => ({
      isPostingPathAdmin: state.user.isPostingPathAdmin,
      listingAlias: state.properties.currentProperty.listingAlias,
      user: state.user,
      currentProperty: state.properties.currentProperty,
    }),
    shallowEqual,
  );

  const [showAboutAddressModal, setShowAboutAddressModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showEditPropertyTypeModal, setShowEditPropertyTypeModal] = useState(false);
  const [addPropertyModalConfig, setAddPropertyModalConfig] = useState<{ isOpen: boolean; behavior?: 'Edit' | 'Add' }>({
    isOpen: false,
  });

  const { data, isPending } = useGetPropertyDetailsOverview(listingAlias);
  const { isArchived, isMultifamily, canSubmitFixMapPinRequest } =
    (data as GetUserPropertyOverviewResponseWebDto) || {};

  const handleArchiveClick = () => setShowArchiveModal(true);
  const handleAboutAddressEditsClick = () => setShowAboutAddressModal(true);
  const handleEditPropertyTypeClick = () => setShowEditPropertyTypeModal(true);

  const handleAdminToolsClick = () => setAddPropertyModalConfig({ isOpen: true, behavior: 'Edit' });

  const handleReportMapPinLocationClick = () => {
    CorrectMapPinCSEvents.ClickReportMapPinLocationFromPropertyDashboard({ user, currentProperty });
  };

  useEffect(
    () => () => {
      setShowEditPropertyTypeModal(false);
      setAddPropertyModalConfig({ isOpen: false });
      setShowArchiveModal(false);
    },
    [],
  );

  return (
    <>
      <Menu
        data-testid="EditPropertyTypeMenu"
        content={
          <Box css={{ zIndex: 'popover' }}>
            <Menu.Item onClick={handleArchiveClick}>{`${isArchived ? 'Unarchive' : 'Archive'} property`}</Menu.Item>
            {Boolean(canSubmitFixMapPinRequest) && (
              <ModalTrigger multiModalTriggerId="MapPinAdjustmentRequestModal">
                <Menu.Item
                  onClick={handleReportMapPinLocationClick}
                  data-eventid={CorrectMapPinCSEvents.ClickReportMapPinLocationFromPropertyDashboard.data_event_id}
                >
                  Report map pin location
                </Menu.Item>
              </ModalTrigger>
            )}
            {!isArchived && !isMultifamily && (
              <>
                <Menu.Item onClick={handleAboutAddressEditsClick}>About address edits</Menu.Item>
                <Menu.Item onClick={handleEditPropertyTypeClick}>Edit property type</Menu.Item>
              </>
            )}
            {isPostingPathAdmin && (
              <Menu.Item onClick={handleAdminToolsClick}>
                <Menu.ItemIcon render={<IconSettingsFilled />} />
                <Menu.ItemLabel css={{ color: 'text.accent.red.strong' }}>Admin tools</Menu.ItemLabel>
              </Menu.Item>
            )}
          </Box>
        }
        trigger={
          <IconButton emphasis="bare" tone="brand" title="Property options" disabled={isPending}>
            <Icon>
              <IconMoreFilled />
            </Icon>
          </IconButton>
        }
      />

      {showArchiveModal && (
        <ArchivePropertyModal
          onClose={() => setShowArchiveModal(false)}
          isArchived={isArchived}
          listingAlias={listingAlias}
          address={{
            street: currentProperty.street,
            city: currentProperty.city,
            state: currentProperty.state,
            zip: currentProperty.zip,
            unit: currentProperty.unit,
          }}
        />
      )}

      {showAboutAddressModal && (
        <AboutAddressEditsModal
          setShowAboutAddressModal={setShowAboutAddressModal}
          setAddPropertyModalConfig={setAddPropertyModalConfig}
        />
      )}

      {showEditPropertyTypeModal && (
        <EditPropertyTypeModal setShowEditPropertyTypeModal={setShowEditPropertyTypeModal} />
      )}

      <AddPropertyModal
        isOpen={addPropertyModalConfig.isOpen}
        behavior={addPropertyModalConfig.behavior}
        onClose={() => setAddPropertyModalConfig({ isOpen: false })}
        user={user}
      />

      <MapPinAdjustmentRequestModal />

      <CloneThisListingConversionModal />
    </>
  );
};

export default EditPropertyTypeMenu;
